import { CheckoutSession } from "lib/api/checkout";
import React from "react";
import { useRouter } from "next/router";
import styled, { css } from "styled-components";
import { SubscriptionShippingWindow } from "@literati/kids-api";
import {
  ImageProps,
  Theme,
  layout,
  media,
} from "@literati/public-ui-shared-react";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import {
  CheckoutSectionCard,
  CheckoutSectionCardHeader,
} from "components/Step/StepPrimitives";
import { NormalizedOrderSummary } from "components/Step/stepTypes";
import useAuthForm from "components/authenticate/useAuthForm";
import PaymentSectionExpectedBy from "components/Payment/PaymentSectionExpectedBy";
import AuthForm from "components/authenticate/AuthForm";
import PaymentSectionOrderDetails from "components/Payment/PaymentSectionOrderDetails";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";
import { MembershipOfferBanner } from "components/Membership/MembershipOfferBanner";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import {
  FALL25_COUPON_CODE,
  getOfferHeadingCssOverride,
  isCouponClaimedByQuery,
} from "components/Step/stepUtil";

const Content = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem 0 0;
  width: 100vw;
  padding-bottom: 2rem;

  ${media.lg} {
    margin: 1.5rem 0 0;
  }
`;

const flexColumnCenter = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledDiv = styled.div`
  ${layout.contentSpacing}
  ${flexColumnCenter}
  max-width: 375px;

  ${media.sm} {
    padding: 0 1.25rem;
  }

  ${media.lg} {
    width: auto;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: 1rem;
    padding: 0 3rem;
    max-width: 896px;
  }
`;

const Column = styled.div`
  ${flexColumnCenter}
  width: 21.5625rem;

  ${media.lg} {
    flex-shrink: 0;
  }
`;

const StyledCheckoutSectionCard = styled(CheckoutSectionCard)`
  margin: 1rem 0 0;
  width: 100%;
`;

const StyledCheckoutSectionCardExpectedBy = styled(StyledCheckoutSectionCard)`
  margin: 0;
  justify-content: space-between;
  gap: 1rem;
`;

const StyledCheckoutSectionCardAuth = styled(StyledCheckoutSectionCard)`
  flex-direction: column;
  align-items: stretch;
  padding: 2.25rem 1.25rem;
`;

const planDetailsLayout = css`
  flex-direction: column;
  align-items: stretch;
  padding: 1.5rem;
  flex-shrink: 1;

  ${media.lg} {
    margin: 0;
  }
`;

const StyledCheckoutSectionCardOrderSummary = styled(StyledCheckoutSectionCard)`
  ${planDetailsLayout}
  padding: 1.5rem;
`;

const StyledHeading = styled.div`
  margin-top: 2rem;
`;

export type StepLayoutPropsCheckout = Omit<
  StepLayoutProps<object>,
  "asForm" | "children" | "formId"
>;

export interface StepCheckoutProps extends StepLayoutPropsCheckout {
  checkoutSession?: CheckoutSession | null;
  onAuthenticationSuccess?: () => void;
  orderSummary?: NormalizedOrderSummary | null;
  selectedPlan?: SubscriptionPlanCamelCase | null;
  selectPlanImageProps?: ImageProps | null;
  shippingWindow?: SubscriptionShippingWindow | null;
}

export const StepCheckout = ({
  checkoutSession,
  onAuthenticationSuccess,
  orderSummary,
  selectedPlan,
  selectPlanImageProps,
  shippingWindow,
  stepFooterProps,
  ...stepLayoutProps
}: StepCheckoutProps) => {
  const router = useRouter();
  const { submitButtonProps: nextButtonProps, authFormProps } = useAuthForm({
    initialFormAction: "createAccount",
  });
  const isFall25CouponClaimed =
    FEATURE_FLAG_SHOW_FALL25_COUPON &&
    isCouponClaimedByQuery(router.query, FALL25_COUPON_CODE);

  const heading = isFall25CouponClaimed ? (
    <div>
      <MembershipOfferBanner isClaimed />
      <StyledHeading>Checkout</StyledHeading>
    </div>
  ) : (
    "Checkout"
  );

  const cssOverride = getOfferHeadingCssOverride(
    isFall25CouponClaimed,
    StepLayoutClassName.HEADING
  );

  return (
    <Theme>
      <StepLayout
        // multiple self-contained forms on page, so we don't use the asForm args
        asForm={false}
        bgColor="papierBlanc"
        css={cssOverride}
        disableContentSpacing
        heading={heading}
        stepFooterProps={{ nextButtonProps, ...stepFooterProps }}
        {...stepLayoutProps}
      >
        <Content>
          <StyledDiv>
            <Column>
              {shippingWindow ? (
                <StyledCheckoutSectionCardExpectedBy>
                  <PaymentSectionExpectedBy shippingWindow={shippingWindow} />
                </StyledCheckoutSectionCardExpectedBy>
              ) : null}
              <StyledCheckoutSectionCardAuth>
                <CheckoutSectionCardHeader>
                  {nextButtonProps.children}
                </CheckoutSectionCardHeader>
                <AuthForm
                  css="margin: 1rem 0 0"
                  flow="full-account"
                  initialFormAction="createAccount"
                  initialValues={{
                    email: checkoutSession?.accountInfo?.email,
                    login: checkoutSession?.accountInfo?.email,
                  }}
                  onSocialLoginSuccess={onAuthenticationSuccess}
                  onSuccess={onAuthenticationSuccess}
                  withTerms={false}
                  {...authFormProps}
                />
              </StyledCheckoutSectionCardAuth>
            </Column>
            <StyledCheckoutSectionCardOrderSummary>
              <PaymentSectionOrderDetails
                boxImageProps={selectPlanImageProps}
                giftCode={checkoutSession?.giftCode}
                orderSummary={orderSummary}
                plan={selectedPlan}
              />
            </StyledCheckoutSectionCardOrderSummary>
          </StyledDiv>
        </Content>
      </StepLayout>
    </Theme>
  );
};

export default StepCheckout;
